import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import Loading from "../helpers/Loading";
import { useSelector } from "react-redux";
import { selectIsLoggedIn } from "../redux/slices/user/userSlice";
import { useTranslation } from "react-i18next";

/* ===========================
   Lazy-loaded pages
=========================== */
const Home = lazy(() => import("../pages/Home"));
const About = lazy(() => import("../pages/About"));
const Contact = lazy(() => import("../pages/Contact"));
const Partners = lazy(() => import("../pages/Partners"));
const Services = lazy(() => import("../pages/Services"));
const NotFound = lazy(() => import("../pages/NotFound"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const PartnerList = lazy(() => import("../components/partners/PartnerList"));
const OnePartner = lazy(() => import("../components/partners/OnePartner"));
const Payment = lazy(() => import("../pages/Payment"));
const Order = lazy(() => import("../components/payment/Order"));
const UserProfilePage = lazy(() => import("../pages/UserProfilePage"));
const ComingSoon = lazy(() => import("../pages/ComingSoon"));
const Login = lazy(() => import("../pages/Login"));

/* ===========================
   Loading helpers
=========================== */
const Load = () => (
  <div>
    <Loading />
  </div>
);

function SuspenseWithLoading({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 200);
    return () => clearTimeout(timer);
  }, [navigate]);

  return isLoading ? <Load /> : <>{children}</>;
}

const withSuspense = (Component) => (
  <Suspense fallback={<Load />}>
    <SuspenseWithLoading>
      <Component />
    </SuspenseWithLoading>
  </Suspense>
);

/* ===========================
   Route Guards
=========================== */

// 🚫 Logged-in users can't access public pages like /login
export const PublicRoute = ({ children }) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const location = useLocation();
  const { i18n } = useTranslation();

  if (isLoggedIn || localStorage.getItem("userData")) {
    return (
      <Navigate to={`/${i18n.language}/`} replace state={{ from: location }} />
    );
  }

  return children;
};

// 🔒 Protect auth routes
export const ProtectedRoute = ({ children }) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const location = useLocation();
  const { i18n } = useTranslation();

  if (!localStorage.getItem("userData")) {
    return (
      <Navigate
        to={`/${i18n.language}/login`}
        replace
        state={{ from: location }}
      />
    );
  }

  return children;
};

/* ===========================
   Base routes (NO language)
=========================== */
const baseRoutes = [
  { path: "", element: withSuspense(Home), auth: false },
  { path: "about", element: withSuspense(About), auth: false },

  {
    path: "login",
    element: <PublicRoute>{withSuspense(Login)}</PublicRoute>,
    auth: false,
  },

  { path: "contact", element: withSuspense(Contact), auth: false },
  { path: "partners", element: withSuspense(Partners), auth: false },

  {
    path: "partners/partner-list/:id?",
    element: withSuspense(PartnerList),
    auth: false,
  },

  {
    path: "partners/partner/:id",
    element: withSuspense(OnePartner),
    auth: false,
  },

  { path: "services", element: withSuspense(Services), auth: false },
  { path: "privacy-policy", element: withSuspense(PrivacyPolicy), auth: false },

  {
    path: "payment/:serviceId/:partnerId",
    element: withSuspense(Payment),
    auth: false,
  },

  {
    path: "payment/order/:serviceId/:partnerId",
    element: withSuspense(Order),
    auth: false,
  },

  {
    path: "my-profile",
    element: <ProtectedRoute>{withSuspense(UserProfilePage)}</ProtectedRoute>,
    auth: true,
  },

  { path: "coming-soon", element: withSuspense(ComingSoon), auth: false },
];

/* ===========================
   Generate language routes
=========================== */
const languages = ["am", "en", "ru"];

const localizedRoutes = languages.flatMap((lng) =>
  baseRoutes.map((route) => ({
    path: `/${lng}${route.path ? `/${route.path}` : ""}`,
    element: route.element,
  })),
);

/* ===========================
   Final routes export
=========================== */
export const routes = [
  // Redirect root → default language
  { path: "/", element: <Navigate to="/am" replace /> },

  // All language routes
  ...localizedRoutes,

  // Catch-all
  { path: "*", element: withSuspense(NotFound) },
];
